import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  deCountriesLocale,
  MessagesDE as deLocale,
  enCountriesLocale,
  MessagesEN as enLocale,
  flattenMessages,
  frCountriesLocale,
  MessagesFR as frLocale,
  getTypedLanguage,
  Language,
  nlCountriesLocale,
  MessagesNL as nlLocale,
} from '@rentguru/commons-utils';
import deDateLocale from 'date-fns/locale/de';
import enDateLocale from 'date-fns/locale/en-US';
import frDateLocale from 'date-fns/locale/fr';
import nlDateLocale from 'date-fns/locale/nl';
import React, { useContext, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { initLanguage, useUser } from '../hooks/UserContext';

export interface ILocaleContext {
  language: Language;
  country: string;
  setCountry: (country: string) => void;
  setLanguage: (language: Language) => void;
  updateLanguage: (lang: Language) => Promise<void>;
  updateCountry: (country: string) => void;
  langAndCountryFormated: string;
}
export const LocaleContext = React.createContext<ILocaleContext | null>(null);
export type dateLocaleMapType = Record<Language, Locale>;
export const dateLocaleMap: Record<Language, Locale> = {
  [Language.EN]: enDateLocale,
  [Language.FR]: frDateLocale,
  [Language.NL]: nlDateLocale,
  [Language.DE]: deDateLocale,
};

const countriesLocaleMap: Record<Language, Record<string, unknown>> = {
  [Language.EN]: enCountriesLocale,
  [Language.FR]: frCountriesLocale,
  [Language.NL]: nlCountriesLocale,
  [Language.DE]: deCountriesLocale,
};
const legalCountryCode: string[] = ['FR', 'DE', 'BE', 'NL'];
export const providerLocaleMap: Record<Language, Record<string, unknown>> = {
  [Language.EN]: enLocale,
  [Language.FR]: frLocale,
  [Language.NL]: nlLocale,
  [Language.DE]: deLocale,
};

export const LocaleProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const initCountry = '';
  const { language: savedLanguage, setUserAttributes } = useUser();
  const rawLanguage = savedLanguage ?? initLanguage;
  let userLanguage: Language;
  try {
    userLanguage = getTypedLanguage(rawLanguage);
  } catch {
    userLanguage = Language.EN;
  }
  const [language, setLanguage] = useState<Language>(userLanguage);
  const [country, setCountry] = useState<string>(initCountry);

  useEffect(() => {
    setLanguage(userLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawLanguage]);

  const updateLanguage = async (lang: Language) => {
    await setUserAttributes({ language: lang });
    setLanguage(lang);
  };
  const updateCountry = (newCountry: string) => {
    if (legalCountryCode.includes(newCountry)) {
      setCountry(newCountry);
    }
  };
  const langAndCountryFormated = country !== '' ? `${language}-${country}` : language;
  const values = React.useMemo(
    () => ({
      language,
      setLanguage,
      country,
      setCountry,
      updateLanguage,
      updateCountry,
      langAndCountryFormated,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language, country, langAndCountryFormated]
  );
  return (
    <LocaleContext.Provider value={values}>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocaleMap[language]}>
        {/* @ts-ignore */}
        <IntlProvider
          key={language}
          locale={language.toLowerCase()}
          messages={{
            ...flattenMessages(providerLocaleMap[language] as Record<string, string>),
            ...flattenMessages(countriesLocaleMap[language] as Record<string, string>),
          }}
          defaultLocale={language.toLowerCase()}
        >
          {children}
        </IntlProvider>
      </MuiPickersUtilsProvider>
    </LocaleContext.Provider>
  );
};
export const useLocale = (): ILocaleContext => {
  const context = useContext<ILocaleContext | null>(LocaleContext);
  if (context === undefined) {
    throw new Error('`useLocale` hook must be used within a `LocaleProvider` component');
  }
  return context as ILocaleContext;
};
